import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Tag, Card, Button, Empty, Modal, Input, Image, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQv4Theme } from "../../helpers";
import { API_DOMAIN } from "../../../constants";
import { cancelPaymentLink } from "../../models/payments/paymentsModel";

dayjs.extend(utc);

const fmtDate = (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—";
const fmtAED = (val) => val != null ? `AED ${Number(val).toLocaleString()}` : "—";

const CANCELLABLE_LINK_STATUSES = new Set(["pending"]);

export function PaymentsTab({ payments, afterSales, parts, canMarkReceived, onMarkReceived, canRecord, onRecord, canOpenPaymentModal, onOpenPaymentModal, userId, onCancelled }) {
  const { t, isDark } = useQv4Theme();
  const hasAfterSales = !!afterSales;
  const partsTotal = (parts ?? [])
    .filter(p => p.selected_quote_id && p.selling_price != null)
    .reduce((s, p) => s + Number(p.selling_price), 0);
  const saleAmount    = hasAfterSales ? Number(afterSales?.sale_amount  || 0) : partsTotal;
  const paidAmount    = hasAfterSales ? Number(afterSales?.paid_amount  || 0) : 0;
  const balanceAmount = hasAfterSales ? Number(afterSales?.balance_amount || 0) : partsTotal;
  const payStatus     = afterSales?.payment_status;

  const statusColor = payStatus === "paid" ? "#389e0d"
    : payStatus === "partial" ? "#d46b08"
    : payStatus === "credit" ? "#1677ff"
    : "#cf1322";

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const doCancelLink = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await cancelPaymentLink(cancelTarget.id, userId, cancelReason.trim() || null);
      if (!res.success) throw new Error(res.message);
      message.success("Payment link cancelled");
      setCancelTarget(null);
      setCancelReason("");
      onCancelled?.();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to cancel payment link");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ background: t.headerBg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>Payment Summary</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {payStatus && (
              <Tag color={payStatus === "paid" ? "green" : payStatus === "partial" ? "orange" : payStatus === "credit" ? "blue" : "red"} style={{ margin: 0, fontSize: 10 }}>
                {afterSales?.payment_status_label || payStatus}
              </Tag>
            )}
            {canRecord && (
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onRecord}>
                Record Payment
              </Button>
            )}
            {canOpenPaymentModal && (!hasAfterSales || balanceAmount > 0) && (
              <Button size="small" type="primary" onClick={onOpenPaymentModal}>
                Payment
              </Button>
            )}
          </div>
        </div>
        {hasAfterSales && balanceAmount <= 0 ? (
          <Tag color="green" style={{ marginTop: 8, fontSize: 11 }}>Fully Paid</Tag>
        ) : afterSales?.remainder_method === "cod" ? (
          <Tag color="gold" style={{ marginTop: 8, fontSize: 11 }}>
            {fmtAED(balanceAmount)} due via Cash on Delivery
          </Tag>
        ) : null}
        <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: `1px solid ${t.border}` }}>
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: t.cardBg }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Invoice{!hasAfterSales ? " (est.)" : ""}</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtAED(saleAmount)}</div>
            </div>
            <div style={{ width: 1, background: t.border }} />
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: isDark ? "rgba(82,196,26,0.12)" : "#f6ffed" }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Collected</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#389e0d" }}>{fmtAED(paidAmount)}</div>
            </div>
            <div style={{ width: 1, background: t.border }} />
            <div style={{ flex: 1, textAlign: "center", padding: "6px 0", background: balanceAmount > 0 ? (isDark ? "rgba(250,173,20,0.12)" : "#fff7e6") : t.cardBg }}>
              <div style={{ fontSize: 10, color: t.textSub, marginBottom: 2 }}>Balance Due</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: balanceAmount > 0 ? statusColor : "#389e0d" }}>
                {fmtAED(balanceAmount)}
              </div>
            </div>
        </div>
      </div>
      {(!payments?.length) && <Empty description="No payments recorded" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "30px 0" }} />}
      {payments.map((p, i) => {
        const proofImages = Array.isArray(p.proof_images) ? p.proof_images : [];
        return (
          <Card key={p.id ?? i} size="small" style={{ borderRadius: 8 }} styles={{ body: { padding: "10px 14px" } }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: "#389e0d" }}>{fmtAED(p.amount)}</span>
                {p.payment_stage && (
                  <Tag color={
                    p.payment_stage === "advance" ? "blue" :
                    p.payment_stage === "balance" ? "purple" :
                    p.payment_stage === "full_payment" ? "green" :
                    p.payment_stage === "refund" ? "red" : "default"
                  } style={{ margin: 0, fontSize: 10 }}>
                    {p.payment_stage_label || p.payment_stage.replace("_", " ")}
                  </Tag>
                )}
              </div>
              <Tag color={p.payment_status === "received" ? "green" : "orange"} style={{ margin: 0, fontSize: 10 }}>
                {p.payment_status_label || p.payment_status}
              </Tag>
            </div>
            <div style={{ fontSize: 11, color: t.textSub, display: "flex", flexDirection: "column", gap: 2 }}>
              {p.payment_method && <span>Method: {p.payment_method}</span>}
              {p.payment_reference && <span>Ref: {p.payment_reference}</span>}
              {p.collected_by && <span>Collected by: {p.collected_by}</span>}
              {p.collection_location && <span>Location: {p.collection_location}</span>}
              {p.paid_at && <span style={{ color: t.textSub }}>{fmtDate(p.paid_at)}</span>}
              {p.remarks && <span style={{ color: t.textSub, marginTop: 2 }}>{p.remarks}</span>}
            </div>
            {proofImages.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Image.PreviewGroup>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {proofImages.map((src, j) => {
                      const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
                      return (
                        <Image key={j} src={url} width={56} height={56}
                          style={{ objectFit: "cover", borderRadius: 4, cursor: "pointer", border: `1px solid ${t.border}` }}
                          preview={{ src: url }}
                        />
                      );
                    })}
                  </div>
                </Image.PreviewGroup>
              </div>
            )}
            {canMarkReceived && p.payment_status !== "received" && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                <Button size="small" type="primary" onClick={() => onMarkReceived(p)}>
                  Mark as Received
                </Button>
              </div>
            )}
            {p.payment_method === "pay_link" && CANCELLABLE_LINK_STATUSES.has(p.payment_status) && (
              <div style={{ marginTop: 10, borderTop: `1px solid ${t.border}`, paddingTop: 10 }}>
                <Button size="small" danger onClick={() => { setCancelTarget(p); setCancelReason(""); }}>
                  Cancel Link
                </Button>
              </div>
            )}
          </Card>
        );
      })}
      <Modal
        open={!!cancelTarget}
        title="Cancel this payment link?"
        okText="Cancel Link"
        okButtonProps={{ danger: true, loading: cancelling }}
        onOk={doCancelLink}
        onCancel={() => setCancelTarget(null)}
        width={420}
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          This marks the link as cancelled in our system. It does not guarantee the customer
          can no longer pay via a stale copy of the link URL at the provider's side.
        </p>
        <Input.TextArea
          rows={2}
          maxLength={300}
          showCount
          placeholder="Optional reason (e.g. customer wants to pay cash instead)"
          value={cancelReason}
          onChange={e => setCancelReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
